<template>
  <page>
    <lazy-div :load="doInit">
      <v-card>
        <v-toolbar dense color="grey lighten-3" flat>
          <v-icon v-if="newMode">mdi-plus</v-icon>
          <v-icon v-else-if="reviewMode">mdi-pencil</v-icon>
          <v-icon v-else-if="queryMode">mdi-magnify</v-icon>
          <v-toolbar-title v-if="newMode">Create New Insured Age Band</v-toolbar-title>
          <v-toolbar-title v-else-if="reviewMode">Edit Insured Age Band</v-toolbar-title>
          <v-toolbar-title v-else-if="queryMode">Query Insured Age Band</v-toolbar-title>
        </v-toolbar>
        <v-card-text>
          <message :messages="messages"></message>
          <v-container fluid grid-list-lg>
            <v-layout row wrap>
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ iabLabel.scheme }}</v-subheader>
                <div class="queryValue" v-for="item in filterSchemeList" :key="item.label">{{ item.label }}</div>
              </v-flex>
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ iabLabel.planNo }}</v-subheader>
                <div class="queryValue">{{ iabForm.planNo }}</div>
              </v-flex>

              <!-- Review -->
              <v-checkbox color="primary" :label="iabLabel.adultInsd" v-model="iabForm.adultInsd" :disabled="!newMode"></v-checkbox>
              <v-flex xs6 v-if="newMode">
                <v-text-field
                  v-model="iabForm.insdAge"
                  :label="iabLabel.insdAge"
                  :error-messages="errors.collect('iabForm.insdAge')"
                  data-vv-name="iabForm.insdAge"
                  :data-vv-as="iabLabel.insdAge"
                  v-validate="'required|numeric'"
                ></v-text-field>
              </v-flex>
              <v-flex xs6 v-else>
                <v-subheader class="queryLabel">{{ iabLabel.insdAge }}</v-subheader>
                <div class="queryValue">{{ iabForm.insdAge }}</div>
              </v-flex>
              <v-flex xs6 v-if="newMode">
                <cal-picker
                  v-model="iabForm.expiryDate"
                  :label="iabLabel.expiryDate"
                  :data-vv-as="iabLabel.expiryDate"
                  data-vv-name="iabForm.expiryDate"
                  :error-messages="errors.collect('iabForm.expiryDate')"
                  v-validate="'required'"
                ></cal-picker>
              </v-flex>
              <v-flex xs6 v-else>
                <v-subheader class="queryLabel">{{ iabLabel.expiryDate }}</v-subheader>
                <div class="queryValue">{{ formatDisplayDate(iabForm.expiryDate) }}</div>
              </v-flex>
              <v-flex xs6 v-if="!queryMode">
                <v-text-field
                  v-model="iabForm.nxtPlanNo"
                  :label="iabLabel.nxtPlanNo"
                  :error-messages="errors.collect('iabForm.nxtPlanNo')"
                  data-vv-name="iabForm.nxtPlanNo"
                  :data-vv-as="iabLabel.nxtPlanNo"
                ></v-text-field>
              </v-flex>
              <v-flex xs6 v-else>
                <v-subheader class="queryLabel">{{ iabLabel.nxtPlanNo }}</v-subheader>
                <div class="queryValue">{{ iabForm.nxtPlanNo }}</div>
              </v-flex>
            </v-layout>

            <v-layout row wrap>
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ iabLabel.createdBy }}</v-subheader>
                <div class="queryValue">{{ iabForm.createdBy }}</div>
              </v-flex>
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ iabLabel.createdDate }}</v-subheader>
                <div class="queryValue">{{ formatDisplayDate(iabForm.createdDate) }}</div>
              </v-flex>
            </v-layout>
            <v-layout row wrap v-if="!newMode">
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ iabLabel.modifiedBy }}</v-subheader>
                <div class="queryValue">{{ iabForm.modifiedBy }}</div>
              </v-flex>
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ iabLabel.modifiedDate }}</v-subheader>
                <div class="queryValue">{{ formatDisplayDate(iabForm.modifiedDate) }}</div>
              </v-flex>
            </v-layout>
            <v-spacer class="mt-3"></v-spacer>
            <v-layout>
              <v-btn color="red darken-1" dark class=" mr-2" @click.native="doCancel">
                Cancel
                <v-icon right dark>mdi-cancel</v-icon>
              </v-btn>
              <v-btn v-if="!queryMode" color="primary" dark class=" mr-2" @click="doSave" :loading="submitting">
                Save
                <v-icon right>mdi-content-save</v-icon>
              </v-btn>
            </v-layout>
          </v-container>
        </v-card-text>
      </v-card>
    </lazy-div>
  </page>
</template>

<script>
import MessageMixin from '@/mixins/message'
import { commonAjaxMixin } from '@/mixins/common/commonAjaxMixin'
import { formatDisplayDate } from '@/utils/date'

export default {
  data() {
    return {
      submitting: false,
      newMode: false,
      reviewMode: false,
      queryMode: false,
      scheme: [],
      iabLabel: {
        scheme: 'Scheme',
        planNo: 'Plan No.',
        adultInsd: 'Adult Insured',
        insdAge: 'Insured Age',
        expiryDate: 'Expiry Date',
        nxtPlanNo: 'Next Plan No.',
        createdBy: 'Created By',
        createdDate: 'Created Date',
        modifiedBy: 'Modified By',
        modifiedDate: 'Modified Date'
      },
      iabForm: {
        scheme: null,
        planNo: null,
        adultInsd: true,
        insdAge: 0,
        expiryDate: undefined,
        nxtPlanNo: undefined,
        createdBy: this.$store.state.token.firstName + ' ' + this.$store.state.token.lastName,
        createdDate: new Date(),
        modifiedBy: null,
        modifiedDate: null
      },
      loading: true,
      today: new Date()
    }
  },
  methods: {
    async doInit() {
      await this.$store.dispatch('packagePlanInit')
      await Promise.all([this.$store.dispatch('commonsInit')])
      this.getCommonDataList('scheme')
      if (this.newMode) {
        this.iabForm.scheme = this.iabId.scheme
        this.iabForm.planNo = this.iabId.planNo
      } else {
        this.fetchData(true)
      }
    },
    async fetchData() {
      const apiURI = this.menu.getDMInsdAgeBand
      if (apiURI) {
        try {
          const expand = {
            scheme: this.iabId.scheme,
            planNo: this.iabId.planNo,
            adultInsd: this.iabId.adultInsd,
            insdAge: this.iabId.insdAge,
            expiryDate: new Date(this.iabId.expiryDate)
          }
          const result = await this.$http.post(apiURI, expand)
          const { data } = result.data
          if (data) {
            this.iabForm.scheme = data.dmInsdAgeBand.scheme
            this.iabForm.planNo = data.dmInsdAgeBand.planNo
            this.iabForm.adultInsd = data.dmInsdAgeBand.adultInsd
            this.iabForm.insdAge = data.dmInsdAgeBand.insdAge
            this.iabForm.expiryDate = data.dmInsdAgeBand.expiryDate
            this.iabForm.nxtPlanNo = data.dmInsdAgeBand.nxtPlanNo
            this.iabForm.createdBy = data.createdBy
            this.iabForm.createdDate = data.createdDate
            this.iabForm.modifiedBy = data.modifiedBy
            this.iabForm.modifiedDate = data.modifiedDate
          }
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        }
      }
    },
    doSave() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          if (this.newIab) {
            this.doCreate()
          } else {
            this.doUpdate()
          }
        } else {
          this.promptFieldCheck()
        }
      })
    },
    async doCreate() {
      const apiURI = this.menu.createDMInsdAgeBand
      if (apiURI) {
        this.submitting = true
        try {
          const result = await this.$http.post(apiURI, this.iabForm)
          const { data } = result
          if (data) {
            this.notify(data)
            this.$router.push({ name: 'insdAgeBandList' })
          }
          this.submitting = false
        } catch (err) {
          this.submitting = false
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        }
      }
    },
    async doUpdate() {
      const apiURI = this.menu.updateDMInsdAgeBand
      if (apiURI) {
        this.submitting = true
        try {
          const result = await this.$http.put(apiURI, this.iabForm)
          const { data } = result
          if (data) {
            this.notify(data)
            this.$router.push({ name: 'insdAgeBandList' })
          }
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        }
      }
    },
    doCancel() {
      this.$router.push({ name: 'insdAgeBandList' })
    },
    formatDisplayDate(xDate) {
      if (xDate === null) {
        return undefined
      } else {
        return formatDisplayDate(xDate)
      }
    }
  },
  created() {
    const jsonData = JSON.parse(sessionStorage.getItem('iab'))
    this.$store.commit('IAB_COMMIT_KEY', jsonData)
    if (this.iabId.tranMode !== '') {
      if (this.iabId.tranMode === 'newMode') {
        this.newMode = true
      } else if (this.iabId.tranMode === 'reviewMode') {
        this.reviewMode = true
      } else if (this.iabId.tranMode === 'queryMode') {
        this.queryMode = true
      }
    }
  },
  computed: {
    menu() {
      return this.$store.state.pkgParam.pkgPlan.menu
    },
    newIab() {
      return this.iabId.tranMode === 'newMode'
    },
    iabId() {
      return this.$store.state.pkgParam.iab.iabId
    },
    filterSchemeList() {
      return this.scheme.filter((list) => {
        return list.value === this.iabForm.scheme
      })
    }
  },
  components: {},
  mixins: [MessageMixin, commonAjaxMixin]
}
</script>
